import React from 'react';
import PropTypes from 'prop-types'; // Recommended for type checking in plain JavaScript/React

/**
 * @typedef {Object} StatsCardProps
 * @property {string} title - The title/label of the statistic (e.g., 'Total Users').
 * @property {string | number} value - The main numerical or string value of the statistic.
 * @property {React.ElementType} icon - The React component for the icon (e.g., UsersIcon from lucide-react).
 * @property {string} description - A supplementary line of text, often for comparison or context (e.g., '20% increase from last month').
 * @property {string} [bgColor='bg-[var(--analytics-card-color)]'] - Tailwind class for the card's background color.
 * @property {string} [textColor='text-black'] - Tailwind class for all text and icon colors within the card.
 * @property {string} [borderColor='border-[var(--border-color2)]'] - Tailwind class for the card's border color.
 * @property {string} [shadow='shadow-lg'] - Tailwind class for the card's shadow.
 */

/**
 * A highly reusable card component designed to display a single, key performance indicator (KPI) or statistic.
 * It features a title, a large value, a descriptive line, and an accompanying icon.
 *
 * @param {StatsCardProps} props - The component props.
 * @returns {JSX.Element} The rendered statistics card component.
 */
const StatsCard = ({
    title,
    value,
    icon: Icon,
    description,
    bgColor = 'bg-[var(--analytics-card-color)]',
    textColor = 'text-black',
    borderColor = 'border-[var(--border-color2)]',
    shadow = 'shadow-lg',
}) => {
    return (
        <div className={`${bgColor} ${shadow} rounded-2xl p-3 border ${borderColor}  `}>
            <div className="flex items-center justify-between mb-4">
                <h3 className={`text-sm font-medium ${textColor}`}>{title}</h3>
                {/* Icon is rendered and styled with the textColor prop */}
                {Icon && <Icon className="w-6 h-6" style={{ color: textColor }} />}
            </div>
            {/* Value is displayed prominently */}
            <div className={`text-2xl font-bold ${textColor} mb-1`}>{value}</div>
            {/* Description provides context */}
            <p className={`text-sm ${textColor}`}>{description}</p>
        </div>
    );
};

export default StatsCard;